import * as React from 'react';
import styled, { css } from 'styled-components';
import { tablet } from '../BreakPoints';
import { Button } from '../button/Button';
import { ButtonRow } from '../button/ButtonRow';
import { AnimatedCollapsibleContent } from '../collapsible/AnimatedCollapsibleContent';
import { colors } from '../colors/color';
import { Icon, iconName } from '../icons/Icons';
import { H2 } from '../typography/Heading';

export type ListRowLevel = 'level1' | 'level2' | 'level3';
export type ListRowSizeVariant = 'fat' | 'medium' | 'slim';
export type ListRowStyleVariant = 'bright' | 'normal' | 'dark' | 'neutral' | 'lightGray';
export type ListRowBorderVariant = 'rounded' | 'sharp' | 'none';

type ListRowWithChildrenProps = ListRowProps &
{
    content?: React.ReactNode,
    childRows?: React.ReactNode;
};

export function ListRowWithChildren (props: ListRowWithChildrenProps): JSX.Element
{
    const [expanded, setExpanded] = React.useState<boolean>(props.expanded);
    React.useEffect(() =>
    {
        if (props.expanded !== undefined)
            setExpanded(props.expanded);
    }, [props.expanded]);

    function handleClickCollapse ()
    {
        const newExpanded = !expanded;
        setExpanded(newExpanded);
        props.onCollapse?.(newExpanded);
    }

    return (
        <>
            <ListRow {...props} onCollapse={props.onCollapse ? handleClickCollapse : undefined}>
                {props.content}
            </ListRow>


            {props.childRows &&
                <AnimatedCollapsibleContent open={expanded == true ? true : false}>
                    {props.childRows}
                </AnimatedCollapsibleContent>
            }
        </>
    );
}

type ListRowProps = {
    stripColor?: string,
    missingStripColorPadding?:boolean, // Left padding for when no strip color is used
    icon?: iconName,
    iconColor?: string,
    expanded?: boolean,
    level?: ListRowLevel,
    sizeVariant?: ListRowSizeVariant,
    styleVariant?: ListRowStyleVariant,
    borderVariant?: ListRowBorderVariant,
    alignItems?: 'flex-start' | 'center' | 'flex-end',
    buttonColumnWidth?: number,
    
    // Button is only shown if onCollapse is defined
    onCollapse?: (expanded: boolean) => void,
    onClick?: () => void,
    children?: React.ReactNode,
};

export function ListRow (props: ListRowProps): JSX.Element
{
    const [expanded, setExpanded] = React.useState<boolean>(false);
    React.useEffect(() =>
    {
        if (props.expanded !== undefined)
            setExpanded(props.expanded);
    }, [props.expanded]);

    function handleClickCollapse (e: React.MouseEvent<HTMLButtonElement>)
    {
        e.stopPropagation();
        const newExpanded = !expanded;
        setExpanded(newExpanded);
        props.onCollapse?.(newExpanded);
    }

    return (
        <StyledRowContainer level={props.level} styleVariant={props.styleVariant || 'bright'} canClick={props.onClick !== undefined} onClick={props.onClick}>

            {props.stripColor &&
                <ColorStrip className="strip_color" color={props.stripColor} styleVariant={props.styleVariant || 'bright'} borderVariant={props.borderVariant || 'rounded'} />
            }
            <StyledRowContent hasStripColor={props.stripColor !== undefined} borderVariant={props.borderVariant || 'rounded'} styleVariant={props.styleVariant || 'bright'} sizeVariant={props.sizeVariant || 'medium'} alignItems={props.alignItems || 'center'}>

                {props.icon &&
                    <Icon name={props.icon} color={props.iconColor !== undefined ? props.iconColor : colors.icon.default} />
                }

                <div style={{ width: '100%', padding: '0 .5rem' }}>
                    {props.children}
                </div>


                <div style={{ width: props.buttonColumnWidth || undefined }}>
                    {props.onCollapse && (
                        <ButtonRow marginLeft={8}>
                            <Button variant='text'
                                icon={expanded ? 'arrows_chevron_chevronUp' : 'arrows_chevron_chevronDown'}
                                onClick={handleClickCollapse} />
                        </ButtonRow>
                    )}

                </div>
            </StyledRowContent>

        </StyledRowContainer>
    );
}

const StyledRowContent = styled.div<{ hasStripColor: boolean, styleVariant: ListRowStyleVariant, sizeVariant: ListRowSizeVariant, borderVariant: ListRowBorderVariant, alignItems: 'flex-start' | 'center' | 'flex-end'; missingStripColorPadding?: boolean; }>`
    display: flex;
    align-content: stretch;
    align-items: ${props => props.alignItems};
    width: 100%;
    gap:0px;

    ${H2}{
        color: ${(props) =>
    {
        if (props.styleVariant === 'dark')
            return colors.text.white;
        else
            return colors.text.gray.dark;
    }} !important;
    }


    ${(props) => props.borderVariant === "rounded" && css`
        border-top-right-radius: 4px;
        border-bottom-right-radius: 4px; 
    `
    }

    ${(props) => props.borderVariant === "sharp" && css`
        border-top-right-radius: 0;
        border-bottom-right-radius: 0; 
    `
    }

    ${(props) => props.styleVariant === 'neutral' && css`
        border: 1px solid ${colors.listRow.neutral.border};
    `
    }

    ${(props) => props.borderVariant === 'sharp' && css`
        border: 0;
        border-bottom: 1px solid ${colors.listRow.neutral.border};
    `
    }

    border-color: ${(props) =>
    {
        if (props.styleVariant === 'normal')
            return colors.listRow.normal.border;
        else if (props.styleVariant === 'bright')
            return colors.listRow.bright.border;
        else if (props.styleVariant === 'lightGray')
            return colors.listRow.lightGray.border;
        else if (props.styleVariant === 'neutral')
            return colors.listRow.neutral.border;
        else
            return colors.listRow.neutral.border;
    }};
    
    ${(props) => props.borderVariant !== 'sharp' && !props.hasStripColor && css`
        border-top-left-radius: 4px;
        border-bottom-left-radius: 4px;
    `}

    ${(props) => props.borderVariant === 'sharp' && !props.hasStripColor && css`
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
    `}

    background-color: ${(props) =>
    {
        if (props.styleVariant === 'normal')
            return colors.listRow.normal.background;
        else if (props.styleVariant === 'bright')
            return colors.listRow.bright.background;
        else if (props.styleVariant === 'dark')
            return colors.listRow.dark.background;
        else if (props.styleVariant === 'lightGray')
            return colors.listRow.lightGray.background;
        else
            return colors.listRow.neutral.background;
    }};

    padding:${(props) =>
    {
        let padding = 0.5;
        if (props.sizeVariant === 'slim')
            padding = 0.2;
        else if (props.sizeVariant === 'fat')
            padding = 2;

        const paddingLeft = padding + (props.hasStripColor ? 0 : (props.missingStripColorPadding ? 0.7 : 0));
        return `${padding}rem ${padding}rem ${padding}rem ${paddingLeft}rem`;
    }};

    font-size: ${props =>
    {
        if (props.sizeVariant === 'slim')
            return '12px';
        if (props.sizeVariant === 'fat')
            return '14px';
        else
            return '14px';
    }};
`;

const StyledRowContainer = styled.div<{ level: ListRowLevel, styleVariant: ListRowStyleVariant, canClick: boolean; }>`
    display: flex;
    flex-direction: row;
    align-content: stretch;
    margin:${(props) =>
    {
        if (props.level === 'level3')
            return '0 0 1px 96px ';
        if (props.level === 'level2')
            return '0 0 1px 48px';
        else
            return '0 0 1px 0';
    }};

    @media (max-width: ${tablet}px) {
        margin:${(props) =>
    {
        if (props.level === 'level3')
            return '0 0 1px 24px ';
        if (props.level === 'level2')
            return '0 0 1px 12px';
        else
            return '0 0 1px 0';
    }};
    }
    

    &:hover ${StyledRowContent}{
        ${props => props.canClick && css`
            cursor:pointer;
        `};
        ${props => props.canClick && props.styleVariant === 'bright' && css`
            background-color: ${colors.listRow.bright.backgroundHover};
        `};
        ${props => props.canClick && props.styleVariant === 'normal' && css`
            background-color:${colors.listRow.normal.backgroundHover};
        `};
        ${props => props.canClick && props.styleVariant === 'neutral' && css`
            background-color: ${colors.listRow.neutral.backgroundHover};
        `};
    }

    &:active{
        cursor: ${props => props.onClick ? 'pointer' : 'default'};
    }
`;

const ColorStrip = styled.div<{ color: string, styleVariant: ListRowStyleVariant, borderVariant: ListRowBorderVariant; }>`
    width: 0.7rem;
    background-color: ${props => props.color};
    
    ${(props) => props.borderVariant !== "sharp" && css`
        border-top-left-radius: 4px;
        border-bottom-left-radius: 4px; 
    `
    }
    
`;